<div class="flex">
  <main class="flex-1" [ngClass.lt-md]="{ mobile: true, subnav__exists: false }">
    <div class="flex" class="webhooks__header">
      <h2 class="flex items-center gap-3">
        <sbnb-icon class="clickable hidden sbnbDesktop:block" [routerLink]="['../']" icon="arrow-left-rose"></sbnb-icon>
        <sbnb-icon class="clickable block sbnbDesktop:hidden" [routerLink]="['../']" icon="left-arrow"></sbnb-icon>
        <span class="flex-1">Webhooks</span>
      </h2>
    </div>

    <section class="mt-6 flex flex-col md:flex-row gap-10">
      <div class="flex-1 flex flex-col md:max-w-[300px]">
        <h3>Introduction</h3>
        <p>
          Webhooks allow your application to receive real-time reservation updates from Hospitable. Instead of
          constantly polling our API for changes, webhooks push data to your application as soon as something
          happens.
        </p>
        <p>Common use cases for webhooks include:</p>
        <ul class="list !pl-4">
          <li>Syncing reservation data with external systems</li>
          <li>Triggering automated workflows when a booking is made or modified</li>
          <li>Updating dashboards or reports in real-time</li>
        </ul>
        <sbnb-button type="secondary" size="mini" class="mt-6">
          <a [href]="learnMoreNextUrl" target="_blank">Learn more</a>
        </sbnb-button>
      </div>

      <div class="flex flex-col flex-1 gap-8">
        <div *ngIf="activeWebhooks?.length > 0" class="flex items-center justify-end">
          <sbnb-button [disabled]="adding" size="mini" (click)="adding = true">+ Add new</sbnb-button>
        </div>

        <div *ngIf="adding" class="flex flex-col border-smoke border rounded p-6 w-full">
          <mat-form-field>
            <input matInput type="text" placeholder="Webhook label" [(ngModel)]="newWebhook.name" />
          </mat-form-field>

          <mat-form-field>
            <input matInput [(ngModel)]="newWebhook.url" placeholder="The URL where this webhook will be sent" />
          </mat-form-field>

          <ng-container>
            <div class="flex flex-col gap-2 mb-4">
              <h4 class="text-sm font-medium">Webhook Types</h4>

              <div class="grid grid-cols-2 gap-2">
                <mat-checkbox
                  *ngFor="let type of webhookEventTypes | keyvalue: originalOrder"
                  [checked]="newWebhook.types?.includes(type.value)"
                  (change)="
                    $event.checked
                      ? newWebhook.types?.push(type.value)
                      : newWebhook.types?.splice(newWebhook.types?.indexOf(type.value), 1)
                  "
                  [attr.data-element]="'webhook-type-' + type.value">
                  <span class="badge badge-grass-clear">{{ type.value | humanize }}</span>
                  <span *ngIf="type.value === WebhookEventType.Messaging" class="badge badge-rose-clear ml-1">Beta</span>
                </mat-checkbox>
              </div>
            </div>
          </ng-container>

          <div class="flex justify-end gap-3">
            <sbnb-button type="secondary" (click)="adding = false">Cancel</sbnb-button>
            <sbnb-button
              [disabled]="!newWebhook.url || !newWebhook.types?.length || saveStatus === 1"
              type="primary"
              (click)="addWebhook()">
              Save
            </sbnb-button>
          </div>
        </div>

        <sbnb-loading-spinner *ngIf="loading"></sbnb-loading-spinner>

        <ng-container *ngIf="!loading">
          <sbnb-no-items
            *ngIf="!activeWebhooks?.length; else content"
            text="No webhooks yet"
            subtext="You haven't created any webhooks yet. You can create the first one with the button below."
            [buttons]="[{ id: 'new', label: '+ Add new', size: 'mini' }]"
            (button)="adding = true"></sbnb-no-items>

          <ng-template #content>
            <div
              *ngFor="let webhook of activeWebhooks"
              class="border-smoke border rounded p-6 pb-12 flex flex-col relative">
              <div class="flex items-center justify-between mb-5">
                <h2 class="input__editable nomargin flex-1">
                  <span
                    #nameSpan
                    style="
                      padding-left: 0;
                      padding-right: 8px;
                      visibility: hidden;
                      position: absolute;
                      left: -9999px;
                    ">
                    {{ webhook.data?.name || 'Unnamed Webhook' }}
                  </span>

                  <input
                    class="input__borderless"
                    type="text"
                    placeholder="Webhook label"
                    [style.width.px]="nameSpan.getBoundingClientRect().width"
                    [(ngModel)]="webhook.data.name"
                    (change)="updateWebhookName(webhook)" />
                </h2>

                <div class="flex items-center gap-2">
                  <div class="flex items-center gap-2">
                    <ng-container *ngIf="!webhook.editingUrl">
                      <sbnb-button
                        size="mini"
                        label="Edit"
                        type="text"
                        (click)="webhook.editingUrl = true"></sbnb-button>

                      <sbnb-button
                        type="secondary"
                        size="mini"
                        [loading]="webhook.testing"
                        loadingLabel="Testing..."
                        [label]="
                          webhook.testResult === null || webhook.testResult === undefined
                            ? 'Test'
                            : webhook.testResult
                              ? 'Success'
                              : 'Fail'
                        "
                        (click)="testWebhook(webhook)"></sbnb-button>
                    </ng-container>

                    <ng-container *ngIf="webhook.editingUrl">
                      <sbnb-button
                        type="secondary"
                        size="mini"
                        label="Cancel"
                        (click)="cancelEditing(webhook)"></sbnb-button>

                      <sbnb-button
                        size="mini"
                        label="Save"
                        type="primary"
                        [disabled]="
                          !webhook.data.url || !(webhook.pendingTypes || webhook.data.types || [])?.length
                        "
                        (click)="updateWebhook(webhook)"></sbnb-button>
                    </ng-container>
                  </div>

                  <button mat-icon-button [matMenuTriggerFor]="menu" aria-label="More options">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #menu="matMenu">
                    <button mat-menu-item (click)="sendHistoricDataPrompt(webhook)">
                      <mat-icon>history</mat-icon>
                      <span>Send historic reservations</span>
                    </button>
                    <button mat-menu-item (click)="deleteWebhookPrompt(webhook)">
                      <mat-icon>delete</mat-icon>
                      <span>Delete</span>
                    </button>
                  </mat-menu>
                </div>
              </div>

              <div class="flex items-center justify-between gap-2 w-full">
                <div class="flex items-center gap-4 flex-1 w-[75%]">
                  <span class="ellipsis leading-8" *ngIf="!webhook.editingUrl">
                    {{ webhook.data.url }}
                  </span>
                  <mat-form-field *ngIf="webhook.editingUrl" class="nopadding w-full">
                    <input
                      matInput
                      type="text"
                      placeholder="The URL where this webhook will be sent"
                      (keydown.enter)="updateWebhook(webhook)"
                      [(ngModel)]="webhook.data.url" />
                  </mat-form-field>
                </div>
              </div>

              <ng-container>
                <div class="flex flex-col gap-2 mt-4">
                  <h4 class="text-sm font-medium">Webhook Types</h4>

                  <ng-container *ngIf="!webhook.editingUrl">
                    <div class="text-sm text-night mt-4" *ngIf="!webhook.data.types?.length">
                      No webhook types selected
                    </div>
                    <div class="flex flex-wrap gap-2 mt-2" *ngIf="webhook.data.types?.length">
                      <span
                        *ngFor="let type of webhook.data.types"
                        class="badge badge-grass-clear"
                        data-element="webhook-type-badge">
                        {{ type | humanize }}
                      </span>
                    </div>
                  </ng-container>

                  <ng-container *ngIf="webhook.editingUrl">
                    <div class="grid grid-cols-2 gap-2">
                      <mat-checkbox
                        *ngFor="let type of webhookEventTypes | keyvalue: originalOrder"
                        [checked]="(webhook.pendingTypes || webhook.data.types || [])?.includes(type.value)"
                        (change)="handleTypeChange(webhook, type.value, $event.checked)"
                        [attr.data-element]="'webhook-type-' + type.value">
                        <span class="badge badge-grass-clear">{{ type.value | humanize }}</span>
                        <span *ngIf="type.value === WebhookEventType.Messaging" class="badge badge-rose-clear ml-1">Beta</span>
                      </mat-checkbox>
                    </div>
                  </ng-container>
                </div>
              </ng-container>

              <div *ngIf="webhook.saveStatus && webhook.saveStatus !== 0" class="absolute right-3 bottom-3">
                <sbnb-save-indicator [saveStatus]="webhook.saveStatus"></sbnb-save-indicator>

                <div *ngIf="webhook.sendStatus === -1" class="flex items-center gap-2">
                  <img src="/assets/iconography/delete.svg" alt="Save Error" />
                  <span class="rose">Failed to send historic data.</span>
                </div>
                <div *ngIf="webhook.sendStatus === 2" class="flex items-center gap-2">
                  <sbnb-icon icon="check" class="grass-darker"></sbnb-icon>
                  <span class="grass-darker">Successfully sent historic data</span>
                </div>
              </div>
            </div>
          </ng-template>
        </ng-container>
      </div>
    </section>
  </main>
</div>
